import React from 'react'
import { UseFormReturn } from 'react-hook-form'
import { H2, Line, Button, Flex } from '../../lib/components'
import { FileUploadIcon, FileUploadedIcon, DeleteIcon } from '../../lib/components/Icon'
import { WebinarFormData } from '../../services/webinar-service'
import '../blogs-form/blogs-form.scss'
import { WEBINAR_STATUSES } from './constants'
import { CircularProgress } from '@mui/material'

type WebinarsFormPresenterProps = {
  form: UseFormReturn<WebinarFormData>
  isEditMode: boolean
  isSubmitting: boolean
  isLoading: boolean
  thumbnailPreview: string
  onSubmit: (data: WebinarFormData) => Promise<void>
  onFileUpload: (file: File) => Promise<void>
  onRemoveThumbnail: () => void
  onTitleBlur: () => void
}

function WebinarsFormPresenter({
  form,
  isEditMode,
  isSubmitting,
  isLoading,
  thumbnailPreview,
  onSubmit,
  onFileUpload,
  onRemoveThumbnail,
  onTitleBlur,
}: WebinarsFormPresenterProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = form

  const thumbnailUrl = watch('thumbnail_url')

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) onFileUpload(file)
    e.target.value = ''
  }

  return (
    <>
      {isLoading && (
        <div className='loading-overlay'>
          <CircularProgress size={40} color='primary' className='loading-overlay__spinner' />
        </div>
      )}

      <Flex variant='column-center'>
        <H2 classes={['mb-24']} text={isEditMode ? 'Edit Webinar' : 'Add New Webinar'} />

        <form className='blog-form' onSubmit={handleSubmit(onSubmit)} noValidate>
          <Line />

          <div className='blog-form__field'>
            <label className='blog-form__label' htmlFor='title'>
              Title <span className='blog-form__required'>*</span>
            </label>
            <input
              id='title'
              className={`blog-form__input ${errors.title ? 'blog-form__input--error' : ''}`}
              placeholder='Enter webinar title'
              maxLength={500}
              {...register('title', {
                required: 'Title is required',
                maxLength: { value: 500, message: 'Title must be at most 500 characters' },
              })}
              onBlur={(e) => {
                register('title').onBlur(e)
                onTitleBlur()
              }}
            />
            {errors.title && <span className='blog-form__error'>{errors.title.message}</span>}
          </div>

          <div className='blog-form__field'>
            <label className='blog-form__label' htmlFor='slug'>
              Slug <span className='blog-form__required'>*</span>
            </label>
            <input
              id='slug'
              className={`blog-form__input ${errors.slug ? 'blog-form__input--error' : ''}`}
              placeholder='webinar-slug'
              maxLength={255}
              {...register('slug', {
                required: 'Slug is required',
                maxLength: { value: 255, message: 'Slug must be at most 255 characters' },
                pattern: {
                  value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                  message: 'Slug must be lowercase letters, numbers, and hyphens only',
                },
              })}
            />
            <span className='blog-form__hint'>
              Unique URL-friendly identifier (auto-generated from title)
            </span>
            {errors.slug && <span className='blog-form__error'>{errors.slug.message}</span>}
          </div>

          <div className='blog-form__field'>
            <label className='blog-form__label' htmlFor='description'>
              Description <span className='blog-form__required'>*</span>
            </label>
            <textarea
              id='description'
              className={`blog-form__input blog-form__textarea ${
                errors.description ? 'blog-form__input--error' : ''
              }`}
              placeholder='Enter webinar description'
              rows={4}
              {...register('description', { required: 'Description is required' })}
            />
            {errors.description && (
              <span className='blog-form__error'>{errors.description.message}</span>
            )}
          </div>

          <div className='blog-form__field'>
            <label className='blog-form__label' htmlFor='youtube_video_id'>
              YouTube video ID <span className='blog-form__required'>*</span>
            </label>
            <input
              id='youtube_video_id'
              className={`blog-form__input ${
                errors.youtube_video_id ? 'blog-form__input--error' : ''
              }`}
              placeholder='e.g. 80g2ICY_R3M'
              maxLength={50}
              {...register('youtube_video_id', {
                required: 'YouTube video ID is required',
                maxLength: { value: 50, message: 'YouTube video ID must be at most 50 characters' },
              })}
            />
            {errors.youtube_video_id && (
              <span className='blog-form__error'>{errors.youtube_video_id.message}</span>
            )}
          </div>

          <div className='blog-form__field'>
            <label className='blog-form__label' htmlFor='published_at'>
              Published date <span className='blog-form__required'>*</span>
            </label>
            <input
              id='published_at'
              type='datetime-local'
              className={`blog-form__input ${errors.published_at ? 'blog-form__input--error' : ''}`}
              {...register('published_at', { required: 'Published date is required' })}
            />
            {errors.published_at && (
              <span className='blog-form__error'>{errors.published_at.message}</span>
            )}
          </div>

          <div className='blog-form__field'>
            <label className='blog-form__label'>Thumbnail (optional)</label>
            <div className='blog-form__file-upload'>
              <label
                className={`blog-form__file-dropzone ${
                  thumbnailUrl ? 'blog-form__file-dropzone--has-file' : ''
                }`}
                htmlFor='thumbnail_url_input'
              >
                {!thumbnailUrl ? (
                  <>
                    <FileUploadIcon />
                    <div className='blog-form__file-info'>
                      <span className='blog-form__file-info-primary'>Browse to upload</span>
                      <span className='blog-form__file-info-secondary'>
                        Supported file types: JPEG, JPG up to 10 MB
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <FileUploadedIcon />
                    <div className='blog-form__file-info'>
                      <span className='blog-form__file-info-primary'>Image uploaded</span>
                    </div>
                    <span
                      className='blog-form__file-delete'
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        onRemoveThumbnail()
                      }}
                    >
                      <DeleteIcon dataTestId='thumbnail-delete-button' />
                    </span>
                  </>
                )}
                <input
                  id='thumbnail_url_input'
                  type='file'
                  accept='image/jpg, image/jpeg'
                  className='blog-form__file-hidden'
                  onChange={handleFileChange}
                />
              </label>
            </div>
            {thumbnailPreview && (
              <div className='blog-form__banner-preview'>
                <img src={thumbnailPreview} alt='Thumbnail preview' />
              </div>
            )}
          </div>

          <div className='blog-form__field'>
            <label className='blog-form__label' htmlFor='status'>
              Status <span className='blog-form__required'>*</span>
            </label>
            <select
              id='status'
              className='blog-form__input'
              {...register('status', { required: 'Status is required' })}
            >
              {WEBINAR_STATUSES.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.text}
                </option>
              ))}
            </select>
            {errors.status && <span className='blog-form__error'>{errors.status.message}</span>}
          </div>

          <Button
            text={isSubmitting ? 'Saving...' : isEditMode ? 'Update Webinar' : 'Create Webinar'}
            variant='primary-btn'
            handleClick={() => handleSubmit(onSubmit)()}
            isDisabled={isSubmitting}
          />
        </form>
      </Flex>
    </>
  )
}

export { WebinarsFormPresenter }
